import { useQueries, useQuery } from '@tanstack/react-query'

import { generateUniqueId } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'
import { type GetProgramInstancePerformanceRequest } from './useGetProgramInstancePerformance'

export const OPERATOR_PROGRAM_INSTANCE_ERRORS_KEY = 'operator-program-instance-errors'

export type ProgramInstanceErrorsResponse = {
  errors: {
    programId: string
    iconType: string | null
    color: string
    displayName: string
    subclass: string | null
    class: string
    mistakenBy: string
    type: 'response' | 'classification' | 'object_detection'
    count: number
    share: number
  }[]
  classifications: {
    programId: string
    iconType: string | null
    color: string
    displayName: string
    class: string
    count: number
    share: number
    accuracy: number
    annotationType: string
  }[]
}

export type ProgramInstanceErrorsQueryResponse = {
  errors: (ProgramInstanceErrorsResponse['errors'][number] & { key: string })[]
  classifications: (ProgramInstanceErrorsResponse['classifications'][number] & { key: string })[]
}

export async function fetchProgramInstanceErrors({
  operatorId,
  programInstanceId,
  startDate,
  endDate,
  scope = 'all',
  timezonePreference,
}: GetProgramInstancePerformanceRequest) {
  const headers = getOperatorPerformanceRequestHeaders(timezonePreference)
  const { data } = await apiClient.get<ProgramInstanceErrorsResponse>(
    `/int/v3/operators/${operatorId}/program-instances/${programInstanceId}/errors`,
    {
      params: { startDate, endDate, scope },
      ...(headers ? { headers } : {}),
    }
  )

  return data
}

function getProgramInstanceErrorsQueryOptions(params: GetProgramInstancePerformanceRequest) {
  const timezoneQueryKeyPart = getOperatorPerformanceQueryTimezonePart(params.timezonePreference)

  return {
    queryKey: [
      OPERATOR_PROGRAM_INSTANCE_ERRORS_KEY,
      params.operatorId,
      params.programInstanceId,
      params.startDate,
      params.endDate,
      params.scope ?? 'all',
      ...(timezoneQueryKeyPart ? [timezoneQueryKeyPart] : []),
    ],
    queryFn: () => fetchProgramInstanceErrors(params),
    select: (data: ProgramInstanceErrorsResponse): ProgramInstanceErrorsQueryResponse => ({
      errors: data.errors.map((error) => ({ ...error, key: generateUniqueId() })),
      classifications: data.classifications.map((classification) => ({
        ...classification,
        key: generateUniqueId(),
      })),
    }),
    enabled: Boolean(params.operatorId) && Boolean(params.programInstanceId),
    refetchOnWindowFocus: false,
  }
}

export function useGetProgramInstanceErrors(params: GetProgramInstancePerformanceRequest) {
  return useQuery(getProgramInstanceErrorsQueryOptions(params))
}

export function useGetProgramInstanceErrorsForPrograms(params: GetProgramInstancePerformanceRequest[]) {
  return useQueries({ queries: params.map(getProgramInstanceErrorsQueryOptions) })
}
